import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, MoveHorizontal } from "lucide-react";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";

const IMG = {
  vorher: "/assets/kreuzberg-vorher-treppenhaus.jpg",
  nachher: "/assets/kreuzberg-nachher-treppenhaus.jpg",
  fassade: "/assets/kreuzberg-fassade.jpg",
  detail1: "/assets/kreuzberg-tuer-detail-1.jpg",
  detail2: "/assets/kreuzberg-tuer-detail-2.jpg",
};

export default function HaustuerKreuzbergstrasse() {
  return (
    <div data-testid="page-haustuer-kreuzbergstrasse">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-12">
        <Link
          to="/referenzen"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8"
          data-testid="haustuer-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zum Portfolio
        </Link>
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
          <span className="inline-block w-2 h-2 bg-accent" />
          Sanierungsgeschichte · Dreiparteienhaus Kreuzbergstraße
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] max-w-4xl">
          Eine neue Haustür <em className="italic">im alten Stil</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
          Der Eingang ist das Gesicht eines Hauses. Beim Dreiparteienhaus in der
          Kreuzbergstraße in Münchberg haben wir ihm seine Würde zurückgegeben –
          mit einer handwerklich gefertigten Holztür in kräftigem Enzianblau.
        </p>
      </section>

      {/* Vorher / Nachher */}
      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <BeforeAfterSlider
          beforeSrc={IMG.vorher}
          afterSrc={IMG.nachher}
          beforeAlt="Hausflur in der Kreuzbergstraße vor der Sanierung mit alter, billiger Tür"
          afterAlt="Derselbe Hausflur nach der Sanierung mit neuer enzianblauer Holztür"
        />
        <p className="mt-4 text-sm text-muted-foreground flex items-center gap-2">
          <MoveHorizontal className="h-4 w-4 text-primary shrink-0" />
          Ziehen Sie den Regler, um den alten Eingang mit der neuen enzianblauen
          Haustür zu vergleichen.
        </p>
      </section>

      {/* Story */}
      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 lg:col-start-2 space-y-6 text-foreground/90 leading-relaxed">
            <p className="text-lg">
              <span className="font-serif text-6xl float-left leading-none mr-3 mt-1 text-primary">A</span>
              ls wir das Haus übernahmen, war die ursprüngliche Eingangstür bereits
              kaputt. An ihrer Stelle steckte eine hässliche, billige Tür, die weder
              zum Haus passte noch ihre Aufgabe richtig erfüllte.
            </p>
            <p>
              Statt einer schnellen Standardlösung haben wir bei einem regionalen
              Handwerksbetrieb eine hochwertige Holztür neu anfertigen lassen –
              maßgeschneidert und im ursprünglichen Stil des Hauses, bis hin zu den
              geätzten Glasfeldern und dem dreiteiligen Oberlicht.
            </p>
            <p>
              Technisch ist die Tür auf dem neuesten Stand: thermisch getrennt für
              eine gute Wärmedämmung und einbruchhemmend für mehr Sicherheit. Der
              Farbton – ein kräftiges Enzianblau – gibt dem Eingang einen
              selbstbewussten, einladenden Auftritt.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 pt-6">
              {[
                { t: "Handwerk aus der Region", b: "Maßgefertigt von einem oberfränkischen Schreinerbetrieb." },
                { t: "Thermisch getrennt", b: "Gute Wärmedämmung im historischen Format." },
                { t: "Einbruchhemmend", b: "Moderne Sicherheit hinter klassischer Optik." },
              ].map((it) => (
                <div key={it.t} className="border-l border-primary/40 pl-4">
                  <h3 className="font-serif text-lg mb-1 text-foreground">{it.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{it.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detail-Galerie */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-8">
          <span className="inline-block w-2 h-2 bg-accent" />
          Im Detail
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl">
          {[
            { src: IMG.fassade, alt: "Hausfassade des Dreiparteienhauses Kreuzbergstraße in Münchberg", caption: "Das Haus" },
            { src: IMG.detail2, alt: "Oberlicht-Detail der enzianblauen Haustür mit Zierkonsolen", caption: "Oberlicht im alten Stil" },
            { src: IMG.detail1, alt: "Nahaufnahme der enzianblauen Holztür mit Edelstahlgriff", caption: "Handwerk im Detail" },
          ].map((d) => (
            <figure key={d.src} className="group" data-testid="haustuer-detail-tile">
              <div className="overflow-hidden">
                <img
                  src={d.src}
                  alt={d.alt}
                  className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                <span className="inline-block w-3 h-px bg-accent" />
                {d.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-secondary text-secondary-foreground px-6 md:px-12 lg:px-24 py-20 md:py-24 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight max-w-3xl mx-auto">
          Werte bewahren heißt für uns: <em className="italic">aufs Detail achten.</em>
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/referenzen">
            <Button className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8 text-sm tracking-wide">
              Weitere Objekte ansehen <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/verkaufen">
            <Button
              variant="outline"
              className="rounded-none border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary h-12 px-8 text-sm tracking-wide"
            >
              Ihr Haus anbieten
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
